// frontend/src/pages/OpeningBalance/OpeningBalance.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  Package, Warehouse, Plus, Building, Calendar, Hash, DollarSign,
  ClipboardList, Upload, FileSpreadsheet, ChevronLeft,
  Download, AlertCircle, CheckCircle, XCircle, Save, X, Loader2,
  ShoppingCart, Truck, RefreshCw, Eye, Filter, Search, Receipt
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../pages/CurrencyContext";
import * as XLSX from "xlsx";
import "./OpeningBalance.css";

// ✅ Backend base URL — same pattern as Banktype.jsx
const url = process.env.REACT_APP_BACKEND_URL;

// ✅ Helper: build auth header from localStorage token
const getAuthHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// ✅ Helper to extract a 3-letter currency code from company object
const getCompanyCurrency = (company) => {
  if (!company) return "USD";
  let currency = null;

  if (company.currency) {
    if (typeof company.currency === "object" && company.currency.code) {
      currency = company.currency.code;
    } else if (typeof company.currency === "string" && /^[A-Z]{3}$/.test(company.currency)) {
      currency = company.currency;
    }
  }

  if (!currency && company.currency_code) {
    if (typeof company.currency_code === "string" && /^[A-Z]{3}$/.test(company.currency_code)) {
      currency = company.currency_code;
    } else if (typeof company.currency_code === "object" && company.currency_code.code) {
      currency = company.currency_code.code;
    }
  }

  if (!currency && company.base_currency) {
    if (typeof company.base_currency === "string" && /^[A-Z]{3}$/.test(company.base_currency)) {
      currency = company.base_currency;
    } else if (typeof company.base_currency === "object" && company.base_currency.code) {
      currency = company.base_currency.code;
    }
  }

  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) {
    currency = "USD";
  }

  return currency;
};

const OpeningBalancePage = () => {
  const { currentUser, userType, company } = useAuth();
  const { formatCurrency: contextFormatCurrency } = useCurrency();
  const navigate = useNavigate();

  // ✅ Company currency
  const companyCurrency = useMemo(() => getCompanyCurrency(company), [company]);

  // ✅ Currency formatter that uses the company's currency
  const formatCurrency = useCallback((amount) => {
    const currencyCode = companyCurrency;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 2,
      }).format(amount || 0);
    } catch {
      return `${currencyCode} ${Number(amount || 0).toFixed(2)}`;
    }
  }, [companyCurrency]);

  // State for bulk upload
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [uploadResults, setUploadResults] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);

  // Pagination state for preview table
  const [previewPage, setPreviewPage] = useState(1);
  const previewRowsPerPage = 5;

  // Data states
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);

  // Calculate user permissions
  const isSystemAdminUser = () => {
    return userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  };

  const isCompanyAdminUser = () => {
    return userType === 'company' && currentUser?.role === 'admin';
  };

  const userCanCreateOpeningBalance = () => {
    return isCompanyAdminUser() || isSystemAdminUser();
  };

  // Get company ID from user context
  const getCompanyId = () => {
    if (userType === 'company' && company) {
      return company._id || company;
    }
    if (currentUser?.companyId) {
      return currentUser.companyId;
    }
    return null;
  };

  // Fetch data function
  const fetchData = async () => {
    if (!userCanCreateOpeningBalance()) {
      toast.error("You don't have permission to create opening balances");
      navigate("/");
      return;
    }

    try {
      setLoading(true);

      const companyId = getCompanyId();

      if (!companyId) {
        toast.error("No company associated with user");
        navigate("/");
        return;
      }

      const headers = {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      };

      // Products
      try {
        const productsResponse = await axios.get(`${url}/api/products/dropdown`, {
          headers,
          params: { company_id: companyId, status: 'active' }
        });

        if (productsResponse.data && Array.isArray(productsResponse.data.data)) {
          const processedProducts = productsResponse.data.data
            .filter(p => p && p._id && (p.product_name || p.name))
            .map(p => ({
              _id: p._id,
              product_name: p.product_name || p.name || 'Unknown Product',
              product_code: p.product_code || p.code || p.product_no || 'N/A',
              status: p.status || 'active'
            }));
          setProducts(processedProducts);
        } else {
          setProducts([]);
        }
      } catch (error) {
        toast.error("Failed to load products");
        setProducts([]);
      }

      // Warehouses
      try {
        const warehousesResponse = await axios.get(`${url}/api/warehouse/dropdown`, {
          headers,
          params: { company_id: companyId, status: 'active' }
        });

        if (warehousesResponse.data && Array.isArray(warehousesResponse.data.data)) {
          const processedWarehouses = warehousesResponse.data.data
            .filter(w => w && w._id && (w.warehouse_name || w.name))
            .map(w => ({
              _id: w._id,
              warehouse_name: w.warehouse_name || w.name || 'Unknown Warehouse',
              warehouse_code: w.warehouse_code || w.code || w.warehouse_id || 'N/A',
              location: w.location || 'N/A',
              status: w.status || 'active'
            }));
          setWarehouses(processedWarehouses);
        } else {
          setWarehouses([]);
        }
      } catch (error) {
        toast.error("Failed to load warehouses");
        setWarehouses([]);
      }

      // Transaction types
      try {
        const transactionTypesRes = await axios.get(`${url}/api/transactiontype/dropdown`, {
          headers,
          params: { company_id: companyId, status: 'active' }
        });

        if (transactionTypesRes.data && Array.isArray(transactionTypesRes.data.data)) {
          setTransactionTypes(transactionTypesRes.data.data);
        } else {
          setTransactionTypes([]);
        }
      } catch (error) {
        setTransactionTypes([]);
      }

    } catch (error) {
      let errorMsg = "Failed to load data";

      if (error.response) {
        if (error.response.status === 401) {
          errorMsg = "Session expired. Please login again.";
          setTimeout(() => navigate("/login"), 2000);
        } else if (error.response.status === 403) {
          errorMsg = "You don't have permission to access this resource";
        } else {
          errorMsg = error.response.data?.message || errorMsg;
        }
      } else if (error.request) {
        errorMsg = "Network error. Please check your connection.";
      }

      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefreshData = async () => {
    toast.info("Refreshing data...");
    await fetchData();
    toast.success("Data refreshed!");
  };

  // Handle file upload
   // Handle file upload
  const handleFileUpload = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    const validExtensions = [".xlsx", ".xls", ".csv"];
    const fileExtension = uploadedFile.name.substring(uploadedFile.name.lastIndexOf(".")).toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      toast.error("Please upload a valid Excel file (.xlsx, .xls, .csv)");
      return;
    }

    setFile(uploadedFile);
    setUploadResults(null);
    setValidationErrors([]);
    setPreviewPage(1);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        // ==========================================
        // ✅ NEW: Duplicate Check Logic
        // ==========================================
        const seenProductNos = new Set();
        const seenBarcodes = new Set();
        const seenProductNames = new Set();
        const duplicateErrors = [];

        jsonData.forEach((row, index) => {
          const excelRow = index + 2; // Excel row number
          
          const productNo = String(row["Product No"] || "").trim().toLowerCase();
          const barcode = String(row["Barcode No"] || "").trim().toLowerCase();
          const productName = String(row["Product Name"] || "").trim().toLowerCase();

          // Check Product No
          if (productNo) {
            if (seenProductNos.has(productNo)) {
              duplicateErrors.push({
                row: excelRow,
                product: row["Product Name"],
                field: "Product No",
                value: row["Product No"],
                message: `Duplicate Product No "${row["Product No"]}" found in Excel file.`
              });
            } else {
              seenProductNos.add(productNo);
            }
          }

          // Check Barcode No
          if (barcode) {
            if (seenBarcodes.has(barcode)) {
              duplicateErrors.push({
                row: excelRow,
                product: row["Product Name"],
                field: "Barcode No",
                value: row["Barcode No"],
                message: `Duplicate Barcode No "${row["Barcode No"]}" found in Excel file.`
              });
            } else {
              seenBarcodes.add(barcode);
            }
          }

          // Check Product Name
          if (productName) {
            if (seenProductNames.has(productName)) {
              duplicateErrors.push({
                row: excelRow,
                product: row["Product Name"],
                field: "Product Name",
                value: row["Product Name"],
                message: `Duplicate Product Name "${row["Product Name"]}" found in Excel file.`
              });
            } else {
              seenProductNames.add(productName);
            }
          }
        });

        const fixedData = jsonData.map((row) => {
          const quantity = Number(row.Quantity || 0);
          const unitPrice = Number(row["Unit Price"] || 0);
          return {
            ...row,
            Total: (quantity * unitPrice).toFixed(2)
          };
        });

        setPreviewData(fixedData);

        // If duplicates were found, show errors and stop
        if (duplicateErrors.length > 0) {
          setValidationErrors(duplicateErrors);
          toast.error(`Found ${duplicateErrors.length} duplicate entries in the file. Please fix them before uploading.`);
        } else {
          toast.info(`File "${uploadedFile.name}" loaded. ${fixedData.length} records found.`);
        }

      } catch (error) {
        toast.error("Error reading Excel file");
      }
    };

    reader.readAsArrayBuffer(uploadedFile);
  };

  // Handle bulk upload
  const handleBulkUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    // Guard: don't even try if there are no warehouses
    if (warehouses.length === 0) {
      toast.error("Create at least one warehouse before uploading opening balances.");
      return;
    }

    // Guard: opening_balance transaction type must be installed
    if (!hasOpeningBalanceType) {
      toast.error(
        "Install the standard transaction types first (opening_balance) before uploading opening balances.",
      );
      return;
    }

    setUploading(true);
    setValidationErrors([]);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${url}/api/openingbalance/excel-upload`, formData, {
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "multipart/form-data",
        },
      });

      const payload = response.data?.data || response.data || {};
      setUploadResults(payload);

      const rowsWithErrors =
        payload.failedDetails ||
        payload.error_details ||
        payload.errorDetails ||
        payload.failed_rows ||
        [];

      if (Array.isArray(rowsWithErrors) && rowsWithErrors.length > 0) {
        const normalized = rowsWithErrors.map((item, idx) => {
          if (typeof item === 'string') {
            return { row: idx + 2, product: '', field: '', value: '', message: item };
          }
          return {
            row: item.row || item.line || idx + 2,
            product:
              item.product ||
              item.product_name ||
              item.record?.['Product Name'] ||
              '',
            field: item.field || '',
            value: item.value ?? '',
            message:
              item.message ||
              (Array.isArray(item.errors) ? item.errors.join('; ') : '') ||
              (typeof item.errors === 'string' ? item.errors : '') ||
              'Unknown error',
          };
        });
        setValidationErrors(normalized);
      } else {
        setValidationErrors([]);
      }

      if (response.data.success) {
        const errorCount =
          (payload.summary?.errors) ||
          (payload.errors) ||
          rowsWithErrors.length ||
          0;

        if (errorCount > 0) {
          toast.info(`Upload finished with ${errorCount} error(s). See details below.`);
        } else {
          toast.success(response.data.message || "Upload completed successfully!");
          setTimeout(() => {
            navigate("/ViewOpeningBalance");
          }, 1500);
        }
      } else {
        toast.error(response.data.message || "Upload failed");
      }
    } catch (error) {
      let errorMsg = "Upload failed";

      if (error.response) {
        errorMsg = error.response.data?.message || errorMsg;
        const payload = error.response.data?.data || {};
        const rowsWithErrors =
          payload.failedDetails ||
          payload.error_details ||
          error.response.data?.errors ||
          [];

        if (Array.isArray(rowsWithErrors) && rowsWithErrors.length > 0) {
          setValidationErrors(
            rowsWithErrors.map((item, idx) => {
              if (typeof item === 'string') {
                return { row: idx + 2, product: '', field: '', value: '', message: item };
              }
              return {
                row: item.row || item.line || idx + 2,
                product: item.product || item.product_name || '',
                field: item.field || '',
                value: item.value ?? '',
                message:
                  item.message ||
                  (Array.isArray(item.errors) ? item.errors.join('; ') : '') ||
                  'Unknown error',
              };
            })
          );
        }
      } else if (error.request) {
        errorMsg = "Network error. Please check your connection.";
      }

      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  // Download template
  const downloadTemplate = () => {
    const templateData = [
      {
        "warehouse_id": 1,
        "Product No": "PROD001",
        "Product Name": "Sample Product",
        "Category Name": "Skincare",
        "UOM": "PCS",
        "UOM Type": "Count",
        "Quantity": 100,
        "Unit Price": 10.50,
        "Sales Price": 15.00,
        "Min Qty": 10,
        "Tax": "NO",
        "Costing Method": "Weighted",
        "Barcode No": "BARCODE001",
        "Transaction Date": new Date().toISOString().split('T')[0],
      },
      {
        "warehouse_id": 2,
        "Product No": "PROD002",
        "Product Name": "Another Product",
        "Category Name": "Fragrance",
        "UOM": "BTL",
        "UOM Type": "Count",
        "Quantity": 50,
        "Unit Price": 25.00,
        "Sales Price": 40.00,
        "Min Qty": 5,
        "Tax": "NO",
        "Costing Method": "Weighted",
        "Barcode No": "BARCODE002",
        "Transaction Date": new Date().toISOString().split('T')[0],
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Opening Balances");

    const wscols = [
      { wch: 14 }, { wch: 14 }, { wch: 28 }, { wch: 18 }, { wch: 10 },
      { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
      { wch: 10 }, { wch: 16 }, { wch: 18 }, { wch: 16 }
    ];
    worksheet["!cols"] = wscols;

    XLSX.writeFile(workbook, "Opening-Balance-Template.xlsx");
    toast.info("Template downloaded successfully!");
  };

  const getActiveProductsCount = () => products.filter(p => p.status === 'active').length;
  const getActiveWarehousesCount = () => warehouses.filter(w => w.status === 'active').length;
  const getActiveProducts = () => products.filter(product => product.status === 'active');
  const getActiveWarehouses = () => warehouses.filter(warehouse => warehouse.status === 'active');

  // Pagination
  const previewTotalPages = Math.max(1, Math.ceil(previewData.length / previewRowsPerPage));
  const paginatedPreviewData = previewData.slice(
    (previewPage - 1) * previewRowsPerPage,
    previewPage * previewRowsPerPage
  );
  const startRow = (previewPage - 1) * previewRowsPerPage + 1;
  const endRow = Math.min(previewPage * previewRowsPerPage, previewData.length);

  const goToPrevPage = () => { if (previewPage > 1) setPreviewPage(previewPage - 1); };
  const goToNextPage = () => { if (previewPage < previewTotalPages) setPreviewPage(previewPage + 1); };

  // ----------------------------------------------------------------
  // Derived state for the setup guards
  // ----------------------------------------------------------------

  // Warehouses
  const hasNoWarehouses = warehouses.length === 0;

  // Products
  const hasNoProducts = products.length === 0;

  // Transaction types — look for the opening_balance standard type.
  // The auto-created "Opening Balance" record also counts, because the
  // standard-setup step will rename it to "opening_balance" on first run.
  const hasOpeningBalanceType = useMemo(() => {
    if (!Array.isArray(transactionTypes)) return false;
    return transactionTypes.some((t) => {
      const name = String(t.transaction_type || "").toLowerCase().trim();
      const ref = String(t.reference_type || "").toUpperCase().trim();
      return name === "opening_balance" || ref === "OPN";
    });
  }, [transactionTypes]);

  // Distinguish "no transaction types at all" vs "types exist but
  // opening_balance is missing" for a more helpful banner message.
  const hasNoTransactionTypes = transactionTypes.length === 0;

  // If every error row is identical, show a single summary explanation above the table
  const distinctErrorMessages = useMemo(() => {
    const set = new Set(
      (validationErrors || [])
        .map((e) => (e.message || '').trim())
        .filter(Boolean)
    );
    return Array.from(set);
  }, [validationErrors]);

  const allErrorsSameCause =
    validationErrors.length > 1 && distinctErrorMessages.length === 1;

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="openingbalance-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!userCanCreateOpeningBalance()) {
    return (
      <div className="form-wrapper">
        <div className="openingbalance-form-container">
          <div className="error-container">
            <AlertCircle size={48} color="#dc3545" />
            <h2>Access Denied</h2>
            <p>You don't have permission to create opening balances.</p>
            <button onClick={() => navigate("/")} className="back-btn">
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="openingbalance-form-container">
        <button
          className="close-btn"
          onClick={() => navigate("/ViewOpeningBalance")}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <ClipboardList size={24} />
          <h2 className="form-title">Bulk Upload Opening Balance</h2>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <p className="form-subtitle">
              {isSystemAdminUser()
                ? "Upload opening balances for any company"
                : `Upload opening balances for ${company?.company_name || 'your company'}`}
            </p>
            <button
              onClick={handleRefreshData}
              style={{
                padding: '8px 16px',
                background: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '14px'
              }}
              title="Refresh dropdown data"
            >
              <RefreshCw size={16} /> Refresh Data
            </button>
          </div>
        </div>

        <div className="bulk-upload-content">
          {/* ============ PRE-UPLOAD WARNING: no warehouses ============ */}
          {hasNoWarehouses && (
            <div
              style={{
                background: '#fff2f0',
                border: '1px solid #ffccc7',
                borderLeft: '4px solid #ff4d4f',
                borderRadius: 6,
                padding: '14px 16px',
                marginBottom: 20,
                color: '#cf1322',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start'
              }}
            >
              <AlertCircle size={22} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <strong style={{ fontSize: 15 }}>
                  You haven't created any warehouses yet.
                </strong>
                <div style={{ marginTop: 4, fontSize: 14 }}>
                  The opening balance template requires a valid <code>warehouse_id</code> for every row.
                  Please create at least one warehouse first, then come back and re-upload.
                </div>
                <button
                  onClick={() => navigate('/ViewWareHouses')}
                  style={{
                    marginTop: 10,
                    padding: '8px 14px',
                    background: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  <Plus size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  Go create a warehouse
                </button>
              </div>
            </div>
          )}

          {/* ============ PRE-UPLOAD WARNING: no products ============ */}
          {!hasNoWarehouses && hasNoProducts && (
            <div
              style={{
                background: '#fffbe6',
                border: '1px solid #ffe58f',
                borderLeft: '4px solid #faad14',
                borderRadius: 6,
                padding: '14px 16px',
                marginBottom: 20,
                color: '#ad6800',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start'
              }}
            >
              <AlertCircle size={22} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <strong style={{ fontSize: 15 }}>No active products found.</strong>
                <div style={{ marginTop: 4, fontSize: 14 }}>
                  Opening balances are linked to products. Create at least one product first.
                </div>
              </div>
            </div>
          )}

          {/* ============ PRE-UPLOAD WARNING: transaction types not installed ============ */}
          {!hasOpeningBalanceType && (
            <div
              style={{
                background: '#fff2f0',
                border: '1px solid #ffccc7',
                borderLeft: '4px solid #ff4d4f',
                borderRadius: 6,
                padding: '14px 16px',
                marginBottom: 20,
                color: '#cf1322',
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start'
              }}
            >
              <AlertCircle size={22} style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <strong style={{ fontSize: 15 }}>
                  {hasNoTransactionTypes
                    ? "Transaction types are not installed."
                    : "The opening_balance transaction type is missing."}
                </strong>
                <div style={{ marginTop: 4, fontSize: 14 }}>
                  Opening balance entries post through the <code>opening_balance</code> transaction
                  type (reference <code>OPN</code>). Please install the standard transaction types
                  for this company first, then come back and re-upload.
                </div>
                <button
                  onClick={() => navigate('/ViewTransactionType')}
                  style={{
                    marginTop: 10,
                    padding: '8px 14px',
                    background: '#ff4d4f',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  <Receipt size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
                  Go install transaction types
                </button>
              </div>
            </div>
          )}

          <div className="upload-instructions">
            <h3 style={{ color: '#121349', marginBottom: '15px' }}>
              <FileSpreadsheet size={18} className="mr-2" />
              Bulk Upload Instructions
            </h3>
            <ol style={{ marginLeft: '20px', marginBottom: '20px', color: '#555' }}>
              <li><strong>Download the template</strong> to see the required format</li>
              <li><strong>Required columns:</strong> warehouse_id, Product No, Product Name, Category Name, UOM, Quantity, Unit Price</li>
              <li><strong>Optional columns:</strong> UOM Type, Sales Price, Min Qty, Tax, Costing Method, Barcode No, Transaction Date</li>
              <li><strong>Warehouse:</strong> must already exist; use its sequential ID (1, 2, 3...) or warehouse code</li>
              <li><strong>Category and UOM:</strong> automatically created from each distinct Excel value</li>
              <li>Fill in your opening balance data</li>
              <li>Upload the completed Excel file (.xlsx, .xls, .csv)</li>
              <li>Review validation results and confirm upload</li>
            </ol>

            <div className="template-section" style={{ marginBottom: '20px' }}>
              <button
                onClick={downloadTemplate}
                className="template-button"
                style={{
                  background: '#667eea',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginBottom: '10px'
                }}
              >
                <Download size={16} /> Download Template
              </button>
              <small style={{ display: 'block', color: '#666' }}>
                Category and UOM values are created automatically. Warehouses must be created first.
              </small>
            </div>
          </div>

          {/* File Upload Section */}
          <div className="upload-section" style={{
            border: '2px dashed #ddd',
            borderRadius: '8px',
            padding: '40px 20px',
            textAlign: 'center',
            marginBottom: '20px',
            opacity: hasNoWarehouses || !hasOpeningBalanceType ? 0.55 : 1
          }}>
            <div className="upload-icon" style={{ marginBottom: '15px' }}>
              <Upload size={48} color="#667eea" />
            </div>
            <h3 style={{ marginBottom: '10px', color: '#121349' }}>Select Excel File</h3>
            <p style={{ color: '#666', marginBottom: '20px' }}>Supported formats: .xlsx, .xls, .csv</p>
            <input
              type="file"
              id="file-upload"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              disabled={uploading || hasNoWarehouses || !hasOpeningBalanceType}
              style={{ display: 'none' }}
            />
            <label
              htmlFor="file-upload"
              style={{
                background: hasNoWarehouses || !hasOpeningBalanceType ? '#bbb' : '#667eea',
                color: 'white',
                padding: '12px 24px',
                borderRadius: '6px',
                cursor: hasNoWarehouses || !hasOpeningBalanceType ? 'not-allowed' : 'pointer',
                display: 'inline-block'
              }}
            >
              Choose File
            </label>

            {file && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                background: '#e8f5e9',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px'
              }}>
                <FileSpreadsheet size={16} color="#4caf50" />
                <span style={{ fontWeight: 'bold' }}>{file.name}</span>
                <small style={{ color: '#666' }}>{(file.size / 1024).toFixed(2)} KB</small>
              </div>
            )}
          </div>

          {/* File Preview */}
          {previewData.length > 0 && (
            <div className="preview-section" style={{
              background: '#f8f9fa',
              border: '1px solid #dee2e6',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <h4 style={{ color: '#495057', margin: 0 }}>
                  <Eye size={16} className="mr-2" />
                  File Preview
                </h4>
                <span style={{ fontSize: '14px', color: '#6c757d' }}>
                  Showing rows {startRow}-{endRow} of {previewData.length}
                </span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ background: '#e9ecef' }}>
                      {Object.keys(previewData[0] || {}).map(key => (
                        <th key={key} style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                          {key}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedPreviewData.map((row, index) => (
                      <tr key={index}>
                        {Object.values(row).map((value, idx) => (
                          <td key={idx} style={{ padding: '8px', border: '1px solid #dee2e6' }}>
                            {value}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {previewData.length > previewRowsPerPage && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '15px',
                  marginTop: '15px',
                  padding: '10px 0'
                }}>
                  <button
                    onClick={goToPrevPage}
                    disabled={previewPage === 1}
                    style={{
                      padding: '6px 14px',
                      background: previewPage === 1 ? '#e9ecef' : '#667eea',
                      color: previewPage === 1 ? '#6c757d' : 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: previewPage === 1 ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: '14px', color: '#495057' }}>
                    Page {previewPage} of {previewTotalPages}
                  </span>
                  <button
                    onClick={goToNextPage}
                    disabled={previewPage === previewTotalPages}
                    style={{
                      padding: '6px 14px',
                      background: previewPage === previewTotalPages ? '#e9ecef' : '#667eea',
                      color: previewPage === previewTotalPages ? '#6c757d' : 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: previewPage === previewTotalPages ? 'not-allowed' : 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ============ PER-ROW ERROR TABLE ============ */}
          {validationErrors.length > 0 && (
            <div
              className="validation-section"
              style={{
                background: '#fff2f0',
                border: '1px solid #ffccc7',
                borderRadius: 8,
                padding: 15,
                marginBottom: 20,
              }}
            >
              <h3 style={{ color: '#cf1322', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={18} />
                {validationErrors.length} row{validationErrors.length === 1 ? '' : 's'} could not be uploaded
              </h3>

              {allErrorsSameCause && (
                <div
                  style={{
                    background: '#fff',
                    border: '1px dashed #ffa39e',
                    borderRadius: 6,
                    padding: '10px 12px',
                    marginBottom: 12,
                    color: '#cf1322',
                    fontSize: 14,
                  }}
                >
                  <strong>Why it failed:</strong> {distinctErrorMessages[0]}
                </div>
              )}

              <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 6 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#ffe7e6' }}>
                      <th style={styles.errTh}>Excel Row</th>
                      <th style={styles.errTh}>Product</th>
                      <th style={styles.errTh}>Field</th>
                      <th style={styles.errTh}>Value</th>
                      <th style={styles.errTh}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validationErrors.map((err, index) => (
                      <tr
                        key={index}
                        style={{ borderBottom: '1px solid #ffd6d3' }}
                      >
                        <td style={styles.errTd}>
                          {err.row || err.line || index + 1}
                        </td>
                        <td style={styles.errTd}>{err.product || '—'}</td>
                        <td style={styles.errTd}>
                          {err.field ? (
                            <code style={{ color: '#cf1322' }}>{err.field}</code>
                          ) : '—'}
                        </td>
                        <td style={styles.errTd}>
                          {err.value !== undefined && err.value !== ''
                            ? String(err.value)
                            : '—'}
                        </td>
                        <td style={styles.errTd}>{err.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p style={{ color: '#cf1322', marginTop: 10, fontSize: 13 }}>
                Fix the rows above and re-upload. Nothing was saved for the failed rows.
              </p>
            </div>
          )}

          {/* Upload Results */}
          {uploadResults && (
            <div className="results-section" style={{
              background: '#d4edda',
              border: '1px solid #c3e6cb',
              borderRadius: '4px',
              padding: '15px',
              marginBottom: '20px'
            }}>
              <h3 style={{ color: '#155724', marginBottom: '15px' }}>
                <CheckCircle size={18} className="mr-2" />
                Upload Results
              </h3>
              <div style={{ display: 'flex', gap: '15px', marginBottom: '15px', flexWrap: 'wrap' }}>
                <div style={{ flex: '1', minWidth: '120px', background: 'white', padding: '15px', borderRadius: '4px', textAlign: 'center' }}>
                  <CheckCircle size={20} color="#28a745" />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0' }}>
                    {uploadResults.summary?.created ?? uploadResults.created ?? 0}
                  </div>
                  <div style={{ color: '#28a745' }}>Created</div>
                </div>
                <div style={{ flex: '1', minWidth: '120px', background: 'white', padding: '15px', borderRadius: '4px', textAlign: 'center' }}>
                  <CheckCircle size={20} color="#17a2b8" />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0' }}>
                    {uploadResults.summary?.updated ?? uploadResults.updated ?? 0}
                  </div>
                  <div style={{ color: '#17a2b8' }}>Updated</div>
                </div>
                <div style={{ flex: '1', minWidth: '120px', background: 'white', padding: '15px', borderRadius: '4px', textAlign: 'center' }}>
                  <XCircle size={20} color="#dc3545" />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0' }}>
                    {uploadResults.summary?.errors ?? uploadResults.errors ?? validationErrors.length ?? 0}
                  </div>
                  <div style={{ color: '#dc3545' }}>Errors</div>
                </div>
                <div style={{ flex: '1', minWidth: '120px', background: 'white', padding: '15px', borderRadius: '4px', textAlign: 'center' }}>
                  <FileSpreadsheet size={20} color="#6c757d" />
                  <div style={{ fontSize: '24px', fontWeight: 'bold', margin: '5px 0' }}>
                    {uploadResults.summary?.total ?? uploadResults.total ?? 0}
                  </div>
                  <div style={{ color: '#6c757d' }}>Total</div>
                </div>
              </div>

              <div style={{ background: '#c3e6cb', padding: '10px', borderRadius: '4px', marginTop: '10px' }}>
                <CheckCircle size={16} color="#155724" className="mr-2" />
                <span style={{ color: '#155724' }}>
                  {uploadResults.summary?.created ?? uploadResults.created ?? 0} records created,{' '}
                  {uploadResults.summary?.updated ?? uploadResults.updated ?? 0} records updated
                </span>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={() => navigate("/ViewOpeningBalance")}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              onClick={handleBulkUpload}
              className="submit-btn"
              disabled={!file || uploading || hasNoWarehouses || !hasOpeningBalanceType || validationErrors.length > 0}
              style={{ minWidth: '150px' }}
              title={
                hasNoWarehouses
                  ? 'Create a warehouse first before uploading opening balances'
                  : !hasOpeningBalanceType
                    ? 'Install the standard transaction types first'
                    : validationErrors.length > 0
                      ? 'Fix the validation errors in the file before uploading'
                      : ''
              }
            >
              {uploading ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="mr-2" size={16} />
                  Upload & Process
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Small helper styles for the error table
const styles = {
  errTh: {
    textAlign: 'left',
    padding: '8px 10px',
    fontWeight: 600,
    color: '#cf1322',
    borderBottom: '1px solid #ffd6d3',
    whiteSpace: 'nowrap',
  },
  errTd: {
    padding: '8px 10px',
    verticalAlign: 'top',
    color: '#191d4c',
  },
};

export default OpeningBalancePage;
